import { TextInput } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';

// How long the field stays quiet before the typed text is handed upwards. Matches
// the 300ms the listing pages already debounce their fetches by, so a burst of
// typing now costs one navigation and one request rather than one of each per
// character.
export const SEARCH_DEBOUNCE_MS = 300;

// Upper bound on how long an emit's echo can take to round-trip back through
// `value` - a caller-side state update and re-render, no network involved, so
// this is a generous ceiling rather than a tuned figure. An emit whose value the
// caller already holds (retype the same term, or the callers' `.toLowerCase()`
// folding a different-cased retype onto the current term) produces no `value`
// change and so no echo ever arrives to decrement `pendingEchoesRef`; without an
// expiry the counter is stuck above zero forever and swallows the next genuine
// external change (Back button, a filter reset) as a phantom echo instead.
export const ECHO_GRACE_MS = 1000;

/**
 * A search input that owns the text being typed.
 *
 * The listing pages keep the search term in the query string and feed it back in
 * through `value`. `HashRouter` wraps every location update in
 * `React.startTransition`, which makes that round trip non-urgent and
 * interruptible: while it is in flight the next keystroke arrives as an urgent
 * update, React re-renders the input against the *last committed* location, and
 * React DOM writes that stale string back over what the user has typed since.
 * Typing faster than the round trip therefore drops characters - measured in
 * Chrome against a listing-sized tree, "abcdefghijklmnop" typed at 30ms
 * intervals arrived as "aekp".
 *
 * So the DOM input is driven by local state, which is always urgent, and `value`
 * is only the *external* value. It is adopted when the field is idle - not
 * focused, no emit pending - and never while one of our own emits is still
 * making its way back around, which is what `pendingEchoesRef` tracks. An emit
 * that lands on a value the caller already holds never produces a `value`
 * change to consume it, so `echoTimeoutsRef` expires it instead of leaving the
 * counter stuck.
 */
const SearchBar = ({ value, onChange, debounceMs = SEARCH_DEBOUNCE_MS }) => {
  // `undefined` means the caller does not track the term at all (ReportsPage
  // filters an already-loaded list), so there is no external value to adopt.
  const isControlled = value !== undefined;

  const [text, setText] = useState(value ?? '');
  const [focused, setFocused] = useState(false);
  const timerRef = useRef(null);

  // Emits handed upwards whose echo has not come back yet. Every emit adds one
  // and every subsequent change to `value` consumes one, so a `value` that is
  // just our own text returning - however many keystrokes behind it now is - is
  // never written back over the field. Tracked separately from `focused`
  // because React only sees a focus event when the window itself is focused, so
  // the focus check alone is not something to rest correctness on.
  //
  // Not every emit gets a `value` change to consume it, though: an emit whose
  // text the caller already holds (retype the same term, or the callers'
  // `.toLowerCase()` folding a different-cased retype onto the current term)
  // never produces one, so nothing would ever decrement the counter back down.
  // `echoTimeoutsRef` is the expiry for those - one grace-period timeout per
  // outstanding emit, in emission order, so a real echo cancels the oldest
  // still-pending one and a no-op emit's just times out on its own.
  const pendingEchoesRef = useRef(0);
  const echoTimeoutsRef = useRef([]);
  const previousValueRef = useRef(value);

  // Emit through the newest `onChange` rather than the one captured when the
  // timer was set: the listing pages build the next query string from the
  // `location` their render closed over, and a debounced emit fires long after
  // that render.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  useEffect(
    () => () => {
      window.clearTimeout(timerRef.current);
      echoTimeoutsRef.current.forEach((id) => window.clearTimeout(id));
    },
    []
  );

  useEffect(() => {
    if (!isControlled) {
      return;
    }
    if (value !== previousValueRef.current) {
      previousValueRef.current = value;
      if (pendingEchoesRef.current > 0) {
        pendingEchoesRef.current -= 1;
        // A real echo landed, so the oldest still-outstanding grace timeout was
        // for it - cancel that one rather than any later emit's.
        const consumedTimeout = echoTimeoutsRef.current.shift();
        if (consumedTimeout !== undefined) {
          window.clearTimeout(consumedTimeout);
        }
        return;
      }
    }
    if (focused || timerRef.current !== null || pendingEchoesRef.current > 0) {
      return;
    }
    setText(value);
  }, [value, focused, isControlled]);

  // The call sites read `e.target.value`, so keep that shape - but carry the text
  // as it was when the emit was scheduled instead of re-reading the live DOM node.
  const emit = (next) => {
    window.clearTimeout(timerRef.current);
    timerRef.current = null;
    pendingEchoesRef.current += 1;
    const timeoutId = window.setTimeout(() => {
      // Nothing consumed this emit's echo within the grace period, so it never
      // arrives - drop it from the queue and give the counter back.
      echoTimeoutsRef.current = echoTimeoutsRef.current.filter((id) => id !== timeoutId);
      pendingEchoesRef.current = Math.max(0, pendingEchoesRef.current - 1);
    }, ECHO_GRACE_MS);
    echoTimeoutsRef.current.push(timeoutId);
    onChangeRef.current?.({ target: { value: next } });
  };

  const handleChange = (event) => {
    const next = event.currentTarget.value;
    setText(next);
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => emit(next), debounceMs);
  };

  // Enter and leaving the field both mean "I am done typing", so don't make the
  // user wait out the debounce for a search they have explicitly finished.
  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && timerRef.current !== null) {
      emit(text);
    }
  };

  const handleBlur = () => {
    setFocused(false);
    if (timerRef.current !== null) {
      emit(text);
    }
  };

  return (
    <TextInput
      value={text}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onFocus={() => setFocused(true)}
      onBlur={handleBlur}
      type="search"
      placeholder="Search here..."
      aria-label="Search"
      rightSection={<IconSearch size={16} />}
    />
  );
};

export default SearchBar;
